import {useCurrentRepository} from '@github-ui/current-repository'
import isHashNavigation from '@github-ui/is-hash-navigation'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLBox} from '@github-ui/safe-html/VerifiedHTML'
import {useNavigate} from '@github-ui/use-navigate'
import {useLocation} from '@github-ui/react-router'
import type React from 'react'
import {memo, useLayoutEffect, useEffect, useImperativeHandle, useRef, useEffectEvent} from 'react'

import {decodeFragmentValue, findElementByFragmentName} from '../utilities/fragment-target'

export interface SharedMarkdownContentProps {
  onAnchorClick?: (event: React.MouseEvent) => void
  richText: SafeHTMLString
  stickyHeaderHeight?: number
  suppressHydrationWarning?: boolean
  className?: string
  ref?: React.Ref<HTMLDivElement>
}

export const SharedMarkdownContent = memo(function SharedMarkdownContent({
  ref,
  onAnchorClick,
  richText,
  stickyHeaderHeight,
  suppressHydrationWarning,
  className,
}: SharedMarkdownContentProps) {
  const {hash} = useLocation()
  const currentRepo = useCurrentRepository()
  const navigate = useNavigate()
  const markdownContainerRef = useRef<HTMLDivElement>(null)

  useImperativeHandle(ref, () => markdownContainerRef.current as HTMLDivElement)

  const handleHashChange = useEffectEvent(() => {
    onHashChange(window.location.hash, stickyHeaderHeight)
  })
  useEffect(() => {
    const hashChange = () => {
      handleHashChange()
    }

    window.addEventListener('load', hashChange)
    window.addEventListener('hashchange', hashChange)
    return () => {
      window.removeEventListener('load', hashChange)
      window.removeEventListener('hashchange', hashChange)
    }
  }, [])

  useLayoutEffect(() => {
    if (markdownContainerRef?.current) {
      handleHashChange()
    }
  }, [hash])

  return (
    <VerifiedHTMLBox
      ref={markdownContainerRef}
      className={`js-snippet-clipboard-copy-unpositioned ${className}`}
      html={richText}
      suppressHydrationWarning={suppressHydrationWarning}
      data-hpc
      // @ts-expect-error React 19 Polymorphic types
      onClick={event => {
        // don't soft navigate if the user cmd/ctrl + click
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
        const isCmdClick = event.metaKey || event.ctrlKey
        const anchor = (event.target as HTMLElement).closest('a')
        if (anchor && anchor.href) {
          if (!isCmdClick) {
            const href = anchor.href
            let url: URL

            try {
              url = new URL(href, window.location.origin)
            } catch {
              // URLs generated by users may be invalid and we should not attempt to navigate in those cases
              return
            }

            if (isHashNavigation(window.location.href, href)) {
              onHashChange(url.hash, stickyHeaderHeight)
              if (window.location.hash === url.hash) {
                event.preventDefault()
              }
            } else if (href.startsWith(`${window.location.origin}/${currentRepo.ownerLogin}/${currentRepo.name}/`)) {
              navigate(url.pathname + url.search + url.hash)
              event.preventDefault()
            }
          }
          onAnchorClick?.(event)
        }
      }}
    />
  )
})

// 125px is the blob sticky header height
export function onHashChange(hash: string, stickyHeaderHeight: number = 125) {
  if (!hash) {
    return
  }

  const targetId = decodeFragmentValue(hash).toLowerCase()
  // a user can generate their own hash links that already include the prefix (typically for cross document links)
  // the majority of targetIds provided to the helper will exclude the prefix
  const normalizedTarget = targetId.startsWith('user-content-') ? targetId : `user-content-${targetId}`
  const target = findElementByFragmentName(document, normalizedTarget) as HTMLElement | null
  if (target && document && document.defaultView) {
    // On Soft Nav, this scrollTo and focus loginc can run essentialy one tick after it's called to ensure elements are in the DOM
    setTimeout(() => {
      window.requestAnimationFrame(() => {
        // Batch layout reads together to minimize forced reflow
        const targetTop = target.getBoundingClientRect().top
        const bodyTop = document.body.getBoundingClientRect().top
        const targetScroll = targetTop - bodyTop - stickyHeaderHeight

        window.scrollTo({top: targetScroll})
        const focusElement = target.closest<HTMLElement>('h1,h2,h3,h4,h5,h6,li,span')
        if (focusElement) {
          focusElement.focus()
          // Set this attribute so soft-navs will autofocus
          focusElement.setAttribute('data-react-autofocus', 'true')
        }
      })
    }, 1)
  }
}
